import React, { useState, ReactElement, ChangeEvent, useEffect } from 'react';
import styled from '@emotion/styled';
import TextInput from './TextInput';
import { getStatusColor } from '../style';
import { IUsernameInputV2Props } from './types/UsernameInputV2';
import FirstnameInfo from './icons/FirstnameInfo';
import FirstnameInfoModal from '../modules/modals/FirstnameInfoModal';
import { FIRSTNAME_FIRST_LETTER_VALIDATOR, FIRSTNAME_VALIDATOR } from '../config/constants';
import { useSelector } from 'react-redux';
import { RootState } from '../store';

const InputWrapper = styled('div')`
  position: relative;
  padding-bottom: 0;
`;

const StatusDescription = styled('div')<{ status: string }>(
  {
    fontSize: 13,
    fontWeight: 400,
    marginBottom: 10,
  },
  ({ status }) =>
    status && {
      color: status == 'error' ? '#E21D4A' : getStatusColor()[status],
    }
);

const UsernameInputV2 = ({
  inputLabel,
  value,
  error,
  status,
  onChange,
  customStyleLabel,
  customInputStyle = {},
  isLogin = false,
  joinForm = false,
  autoFocus = false,
  customStyle = {},
  placeholder = '',
  isRequired,
  onFocus,
  showInfoIcon = false,
  showErrorAtTop = false,
  customStyleLabelContainer = {},
  customStatusDescriptionStyle = {},
}: IUsernameInputV2Props): ReactElement => {
  const [fieldError, setFieldError] = useState('');
  const [showInfoModal, setShowInfoModal] = useState(false);
  const username = useSelector((state: RootState) => state.profile.username);

  useEffect(() => {
    const isValidStartLetter = FIRSTNAME_FIRST_LETTER_VALIDATOR.test(value);
    const isValidLetter = value?.match(FIRSTNAME_VALIDATOR);

    if (value && (!isValidStartLetter || !isValidLetter)) {
      setFieldError('Please use your first name or nickname');
    }
  }, []);

  const validateUsername = (value: string) => {
    let inputError = '';

    const isValidStartLetter = FIRSTNAME_FIRST_LETTER_VALIDATOR.test(value);
    const isValidLetter = value.match(FIRSTNAME_VALIDATOR);

    if (value.length < 3 || value.length > 16) {
      inputError = 'First name must be 3 to 16 characters long';
    } else if (!isValidLetter) {
      inputError = 'Please only use letters';
    } else if (!isValidStartLetter) {
      inputError = 'First name must start with a letter. No special characters or spaces.';
    } else if (username?.toLowerCase() === value?.toLowerCase()) {
      inputError = 'Please provide a new First Name option';
    }

    setFieldError(inputError);
  };

  const handleOnTextChange = (e: ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;

    validateUsername(inputValue);

    onChange(e);
  };

  const fieldStatus = (fieldError && 'error') || status;

  return (
    <>
      <InputWrapper>
        {showErrorAtTop && (
          <StatusDescription data-test-id={`${status}-username`} status={fieldStatus}>
            {fieldError || error}
          </StatusDescription>
        )}
        <TextInput
          id="username"
          inputLabel={inputLabel}
          name="username"
          value={value || ''}
          onChange={handleOnTextChange}
          onBlur={handleOnTextChange}
          status={fieldStatus}
          statusMessage={(!showErrorAtTop && fieldError) || error}
          customStyle={{ maxWidth: '452px', ...customStyle }}
          customInputStyle={{ ...customInputStyle, fontSize: 14 }}
          customStyleLabel={customStyleLabel}
          isLogin={isLogin}
          joinForm={joinForm}
          autoFocus={autoFocus}
          placeholder={placeholder}
          hideStatusIcon={true}
          isRequired={isRequired}
          onFocus={onFocus}
          extraLabelInfo={
            showInfoIcon && (
              <>
                <FirstnameInfo style={{ cursor: 'pointer' }} onClick={() => setShowInfoModal(true)} />
              </>
            )
          }
          customStyleLabelContainer={customStyleLabelContainer}
          customStatusDescriptionStyle={customStatusDescriptionStyle}
        />
      </InputWrapper>
      <FirstnameInfoModal isOpen={showInfoModal} onClose={() => setShowInfoModal(false)} />
    </>
  );
};

export default React.memo(UsernameInputV2);
